import { FormArray, FormGroup } from '@angular/forms';

import { UtilService } from '@shared/services/util.service';

/**
 * Contas de dinheiro da seção Pagamento da NF-e (cópia de `venda-pagamento.calculo.ts`, D34):
 * cobertura dos recebimentos, rateio por centro de custo e conversão para centavos. Tudo em 2
 * casas ABNT (standards/backend/dfe.md, seção 9: fatura, parcelas e rateio usam 2 casas fixas).
 */

/** Um valor em reais como inteiro de centavos, decidido pelo ABNT em 2 casas. */
export function centavos(valor: number): number {
    return Math.round(UtilService.arredondarAbnt(Number(valor) || 0, 2) * 100);
}

/** Σ do `valorPagamento` dos recebimentos, 2 casas ABNT. */
export function somarRecebimentos(recebimentos: FormGroup[]): number {
    return UtilService.arredondarAbnt(
        recebimentos.reduce((soma, r) => soma + (Number(r.get('valorPagamento')?.value) || 0), 0),
        2,
    );
}

/** O "Lançado" do resumo: Σ do `valorPagamento` do FormArray `pagamentos`, 2 casas ABNT. */
export function calcularLancado(pagamentos: FormArray): number {
    return somarRecebimentos((pagamentos?.controls ?? []) as FormGroup[]);
}

/** `total − lançado` em centavos: positivo falta, negativo excede, zero cobre. */
export function diferencaEmCentavos(totalDoDocumento: number, lancado: number): number {
    return centavos(totalDoDocumento) - centavos(lancado);
}

/** Σ do `percentual` das linhas do rateio, 2 casas ABNT. */
export function somarPercentuaisDoRateio(linhas: FormGroup[]): number {
    return UtilService.arredondarAbnt(
        linhas.reduce((soma, l) => soma + (Number(l.get('percentual')?.value) || 0), 0),
        2,
    );
}

/**
 * Valores do rateio a partir dos percentuais, em centavos, com o resto na ÚLTIMA linha quando os
 * percentuais fecham 100%: Σ R$ = total por construção.
 */
export function distribuirRateioEmCentavos(totalDoDocumento: number, percentuais: number[]): number[] {
    if (!percentuais.length) return [];

    const totalC = centavos(totalDoDocumento);
    const somaPercentual = UtilService.arredondarAbnt(percentuais.reduce((a, p) => a + (Number(p) || 0), 0), 2);
    // Fecha 100% só quando a soma, a 2 casas, é exatamente 100 (99,99% não fecha).
    const fechaCem = somaPercentual === 100;
    let distribuido = 0;

    return percentuais.map((percentual, i) => {
        const ehUltima = i === percentuais.length - 1;
        const c = ehUltima && fechaCem
            ? totalC - distribuido
            : centavos((totalDoDocumento * (Number(percentual) || 0)) / 100);

        distribuido += c;

        return c / 100;
    });
}

/** O percentual de uma linha do rateio a partir do valor digitado, 2 casas ABNT. */
export function percentualDoRateio(valor: number, totalDoDocumento: number): number {
    if (totalDoDocumento <= 0) return 0;

    return UtilService.arredondarAbnt(((Number(valor) || 0) / totalDoDocumento) * 100, 2);
}
